// treeSlice.ts
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { createDiffUtil } from 'store/util/diff';
import {
	applyTreeHistorySnapshot,
	createTreeHistorySnapshot,
	ensureTreeHistoryBase,
	pushTreeHistory,
	type TreeHistorySnapshot,
} from './history';
import { pointsToPolygon } from 'components/tool/utils';

// --- ID Types ---
type TempId = string;
type SavedId = number;
export type ShapeId = string | number;

// --- Pending changes container ---
// "added"   => keyed by TempId  (not yet persisted)
// "updated" => keyed by SavedId (already persisted, being edited)
export type PendingChanges<T> = {
  added: Record<TempId, T>;
  updated: Record<SavedId, Partial<T>>;
};

// Local editing representation: the API's `trunkHeight` is replaced with the
// total `treeHeight` so the trunk absorbs changes automatically (trunk =
// treeHeight - crownHeight). `treeHeight` only changes when edited via its
// own field; editing crownHeight never touches it. Converted back to
// trunkHeight at the API boundary (see useEditDsm.ts / roofSelector.ts).
export type Tree = Omit<TreeType, 'trunkHeight'> & {
	id: ShapeId;
	treeHeight: number; // in m
}

// Intentionally NOT clamped to 0 — callers (mesh building, ground-clip math)
// rely on the raw, possibly-negative value to keep the crown's peak pinned
// at treeHeight above ground, and to know how much of the crown to clip at
// its base once crownHeight exceeds treeHeight. Clamping here breaks that
// model silently: it caps the group's vertical *offset*, not just the
// visible trunk mesh, which makes the tree's apparent total height grow
// along with crownHeight instead of staying fixed.
// Any caller that needs a non-negative trunk mesh height (e.g. cylinder
// height for rendering the trunk) should clamp locally instead:
// `Math.max(0, getTrunkHeight(tree))` — which is exactly what
// buildSingleTreeMesh already does via `trunkHeightVisible`.
export const getTrunkHeight = (tree: Pick<Tree, 'treeHeight' | 'crownHeight'>): number =>
	tree.treeHeight - tree.crownHeight;

export type FlattenArea = FlattenAreaType & {
	id: ShapeId;
	shape?: FlattenShape;
}

export type FlattenShape = 'polygon' | 'circle' | 'free';

interface TreeState {
	trees: PendingChanges<Tree>;
	deleted: number[];
	is3DEnabled: boolean,
	treeEditingMode: boolean,
	deleteMode: boolean,
	isDrawingTree: boolean,
	selectedShapeId: ShapeId | null, // can be tree/flatten area polygon id
	isFlattenAreaMode: boolean;
	flattenShape: FlattenShape;
	flattenArea: PendingChanges<FlattenArea>;
	editedFlattenIds: Record<string, true>;
	draftFlatten: {
		points: number[];
	};
	isDraftingTree: boolean;
	history: TreeHistorySnapshot[];
	currentIndex: number;
	isTreeDoneLoading: boolean;
	loadingFlattenIds: Record<string, boolean>;
}

export const TreeDiff = createDiffUtil<Tree>();
export const FlattenAreaDiff = createDiffUtil<FlattenArea>();

export const hasPendingChanges = <T,>(changes: PendingChanges<T>) =>
	Object.keys(changes.added).length > 0 || Object.keys(changes.updated).length > 0;

const resetTreeEditState = (data: TreeState) => {
	data.trees = {
		added: {},
		updated: {},
	};
	data.flattenArea = {
		added: {},
		updated: {},
	};
	data.editedFlattenIds = {};
	data.deleted = [];
};

const initialState: TreeState = {
	trees: {
		added: {},
		updated: {},
	},
	flattenArea: {
		added: {},
		updated: {},
	},
	deleted: [],
	is3DEnabled: false,
	treeEditingMode: false,
	deleteMode: false,
	isDrawingTree: false,
	selectedShapeId: null,
	isFlattenAreaMode: false,
	flattenShape: 'free',
	editedFlattenIds: {},
	draftFlatten: {
		points: [],
	},
	isDraftingTree: false,
	history: [],
	currentIndex: 0,
	isTreeDoneLoading: false,
	loadingFlattenIds: {},
};

const treeSlice = createSlice({
	name: 'tree',
	initialState: {
		data: initialState,
	},
	reducers: {
		addTree(state, action: PayloadAction<Tree>) {
			ensureTreeHistoryBase(state.data);

			let draftStartIndex = -1;
			if (state.data.isDraftingTree) {
				for (let i = state.data.currentIndex; i >= 0; i -= 1) {
					if (!state.data.history[i]?.isDraftingTree) {
						draftStartIndex = i;
						break;
					}
				}
			}

			state.data.trees.added[action.payload.id] = action.payload;
			state.data.isDraftingTree = false;

			if (draftStartIndex >= 0) {
				const snapshot = createTreeHistorySnapshot(state.data);
				state.data.history = [...state.data.history.slice(0, draftStartIndex + 1), snapshot];
				state.data.currentIndex = state.data.history.length - 1;
				return;
			}

			pushTreeHistory(state.data);
		},
		toggle3DView(state, action: PayloadAction<boolean>) {
			state.data.is3DEnabled = action.payload;
		},
		commitTreeEdits(state) {
			resetTreeEditState(state.data);
			state.data.history = [];
			state.data.currentIndex = 0;
		},
		toogleTreeEditingMode(state, action: PayloadAction<boolean>) {
			state.data.treeEditingMode = action.payload;
			if (!action.payload) {
				resetTreeEditState(state.data);
				state.data.deleteMode = false;
				state.data.isDrawingTree = false;
				state.data.selectedShapeId = null;
				state.data.isFlattenAreaMode = false;
				state.data.flattenShape = 'free';
				state.data.draftFlatten = {
					points: [],
				};
				state.data.isDraftingTree = false;
				state.data.history = [];
				state.data.currentIndex = 0;
			}
		},
		toggleDeleteTreesMode(state, action: PayloadAction<boolean>) {
			state.data.deleteMode = action.payload;
			if (action.payload) {
				state.data.isDrawingTree = false;
				state.data.selectedShapeId = null;
			}
		},
		setIsDrawingTree(state, action: PayloadAction<boolean>) {
			state.data.isDrawingTree = action.payload;
		},
		setSelectedShapeId(state, action: PayloadAction<ShapeId | null>) {
			state.data.selectedShapeId = action.payload;
		},
		setDeleted(state, action: PayloadAction<string | number>) {
			ensureTreeHistoryBase(state.data);
			const id = action.payload;
			if (typeof id === 'string') { // not yet saved;
				delete state.data.trees.added[id];
				delete state.data.flattenArea.added[id];
			} else {
				state.data.deleted = [...state.data.deleted, id];
			}
			delete state.data.editedFlattenIds[String(id)];
			pushTreeHistory(state.data);
		},
		updateTree(state, action: PayloadAction<{ id: number | string, tree: Partial<Tree> }>) {
			ensureTreeHistoryBase(state.data);
			const id = action.payload.id;
			if (id in state.data.trees.added) {
				state.data.trees.added[id] = {
					...state.data.trees.added[id],
					...action.payload.tree
				};
				pushTreeHistory(state.data);
				return;
			}
			const existingTreeId = id as number;
			state.data.trees.updated[existingTreeId] = {
				...state.data.trees.updated[existingTreeId],
				...action.payload.tree
			};
			pushTreeHistory(state.data);
		},
		toggleFlattenAreaMode(state, action: PayloadAction<boolean>) {
			state.data.isFlattenAreaMode = action.payload;
			if (action.payload) {
				state.data.flattenShape = 'free';
			}
		},
		setFlattenShape(state, action: PayloadAction<FlattenShape>) {
			state.data.flattenShape = action.payload;
		},
		setEditingPolygonId(state, action: PayloadAction<string | null>) {
			state.data.selectedShapeId = action.payload;
		},
		setFlattenPolygon(state, action: PayloadAction<FlattenArea>) {
			ensureTreeHistoryBase(state.data);
			// If we were drawing a draft polygon, squash all point-by-point draft history
			// into a single "polygon added" entry so undo removes the full polygon at once.
			let draftStartIndex = -1;
			for (let i = state.data.currentIndex; i >= 0; i -= 1) {
				if (state.data.history[i]?.draftFlattenPoints.length === 0) {
					draftStartIndex = i;
					break;
				}
			}

			state.data.flattenArea.added[action.payload.id] = action.payload;
			state.data.draftFlatten = { points: [] };

			if (draftStartIndex >= 0) {
				const snapshot = createTreeHistorySnapshot(state.data);
				state.data.history = [...state.data.history.slice(0, draftStartIndex + 1), snapshot];
				state.data.currentIndex = state.data.history.length - 1;
				return;
			}

			pushTreeHistory(state.data);
		},
		updateFlattenPolygonPoints(state,action: PayloadAction<{ id: ShapeId; points: number[] }>) {
			ensureTreeHistoryBase(state.data);
			const { id, points } = action.payload;
			if (id in state.data.flattenArea.added) {
				state.data.flattenArea.added[id as TempId].coords = pointsToPolygon(points);
			} else {
				state.data.flattenArea.updated[id as SavedId] = {
					...(state.data.flattenArea.updated[id as SavedId] ?? {}),
					coords: pointsToPolygon(points)
				};
			}
			state.data.editedFlattenIds[String(id)] = true;
			pushTreeHistory(state.data);
		},
		setFlattenHeight(state,action: PayloadAction<{ id: ShapeId; height: number }>) {
			ensureTreeHistoryBase(state.data);
			const { id, height } = action.payload;
			if (id in state.data.flattenArea.added) {
				state.data.flattenArea.added[id as TempId].height = height;
			} else {
				state.data.flattenArea.updated[id as SavedId] = {
					...(state.data.flattenArea.updated[id as SavedId] ?? {}),
					height, 
				};
			}
			pushTreeHistory(state.data);
		},
		addFlattenDraftPoint(state, action: PayloadAction<{ point: { x: number; y: number } }>) {
			ensureTreeHistoryBase(state.data);
			const { x, y } = action.payload.point;
			state.data.draftFlatten.points = [...state.data.draftFlatten.points, x, y];
			pushTreeHistory(state.data);
		},
		resetFlattenDraft(state) {
			ensureTreeHistoryBase(state.data);
			state.data.draftFlatten = {
				points: [],
			};
			pushTreeHistory(state.data);
		},
		setDraftTree(state) {
			ensureTreeHistoryBase(state.data);
			state.data.isDraftingTree = true;
			pushTreeHistory(state.data);
		},
		cancelDraftTree(state) {
			if (!state.data.isDraftingTree) return;
			let draftStartIndex = -1;
			for (let i = state.data.currentIndex; i >= 0; i -= 1) {
				if (!state.data.history[i]?.isDraftingTree) {
					draftStartIndex = i;
					break;
				}
			}
			state.data.isDraftingTree = false;
			if (draftStartIndex >= 0) {
				state.data.history = state.data.history.slice(0, draftStartIndex + 1);
				state.data.currentIndex = draftStartIndex;
			}
		},
		undoTreeEdits(state) {
			if (state.data.currentIndex > 0) {
				state.data.currentIndex -= 1;
				const snapshot = state.data.history[state.data.currentIndex];
				if (snapshot) {
					applyTreeHistorySnapshot(state.data, snapshot);
				}
			}
		},
		redoTreeEdits(state) {
			if (state.data.currentIndex < state.data.history.length - 1) {
				state.data.currentIndex += 1;
				const snapshot = state.data.history[state.data.currentIndex];
				if (snapshot) {
					applyTreeHistorySnapshot(state.data, snapshot);
				}
			}
		},

		setTreeDoneLoading(state, action: PayloadAction<boolean>) {
			state.data.isTreeDoneLoading = action.payload;
		},
		setFlatPxAndFacetAffectedPx(state,action: PayloadAction<{
			id: string; 
			facet_affected_px: FlattenArea['facet_affected_px'],
			flat_px: FlattenArea['flat_px']
			ground_z: number;
		}>) {
			const { id, facet_affected_px, flat_px , ground_z} = action.payload;
			if (id in state.data.flattenArea.added) {
				state.data.flattenArea.added[id as TempId].flat_px = flat_px;
				state.data.flattenArea.added[id as TempId].facet_affected_px = facet_affected_px;
				state.data.flattenArea.added[id as TempId].ground_z = ground_z;
				// Update history snapshots so undos don't lose the fringes if they arrive
				// after a subsequent action (like a height change) has already taken a snapshot.
				state.data.history.forEach(snapshot => {
					if (id in snapshot.flattenArea.added) {
						snapshot.flattenArea.added[id].flat_px = flat_px;
						snapshot.flattenArea.added[id].facet_affected_px = facet_affected_px;
						snapshot.flattenArea.added[id].ground_z = ground_z;
					}
				});
			}
		},
		setFlattenLoading(state, action: PayloadAction<{ id: string; loading: boolean }>) {
			if (action.payload.loading) {
				state.data.loadingFlattenIds[action.payload.id] = true;
			} else {
				delete state.data.loadingFlattenIds[action.payload.id];
			}
		},
		setFlattenApiFailed(state, action: PayloadAction<string>) {
			state.data.editedFlattenIds[action.payload] = true;
		},
	},
	
});

export const {
	addTree,
	toggle3DView,
	commitTreeEdits,
	toogleTreeEditingMode,
	toggleDeleteTreesMode,
	setIsDrawingTree,
	setSelectedShapeId,
	setDeleted,
	updateTree,
	toggleFlattenAreaMode,
	setFlattenShape,
	setEditingPolygonId,
	setFlattenPolygon,
	updateFlattenPolygonPoints,
	setFlattenHeight,
	addFlattenDraftPoint,
	resetFlattenDraft,
	setDraftTree,
	cancelDraftTree,
	undoTreeEdits,
	redoTreeEdits,
	setFlatPxAndFacetAffectedPx,
	setTreeDoneLoading,
	setFlattenLoading,
	setFlattenApiFailed,
} = treeSlice.actions;
export default treeSlice.reducer;
